import { findOrderInversions, type OrderItem } from './sankey-inversions';
import {
  LINEAR_SANKEY_SCALE,
  MIN_LINK_WIDTH,
  scaleLinkValues,
} from './sankey-layout';
import type { SankeyScale } from './types';

const EXPONENT_MIN = 0.1;
const EXPONENT_MAX = 1;
const EXPONENT_STEP = 0.05;
const EXPONENT_STEPS = Math.round(
  (EXPONENT_MAX - EXPONENT_MIN) / EXPONENT_STEP
);

type SankeyLinkEnds = { source: string; target: string };

export type SolveScaleInput = {
  values: number[];
  sideKeys: (string | undefined)[];
  linkGroups: (string | undefined)[];
  linkEnds: SankeyLinkEnds[];
  nodeGroups: Map<string, string>;
  plotHeight: number;
  nodePadding: number;
};

type NodeTotals = { in: number; out: number };

const nodeTotalsFor = (
  linkEnds: SankeyLinkEnds[],
  values: number[]
): Map<string, NodeTotals> => {
  const totals = new Map<string, NodeTotals>();
  const bump = (id: string, side: 'in' | 'out', value: number) => {
    const entry = totals.get(id) ?? { in: 0, out: 0 };
    entry[side] += value;
    totals.set(id, entry);
  };
  linkEnds.forEach((ends, index) => {
    const value = values[index] ?? 0;
    bump(ends.source, 'out', value);
    bump(ends.target, 'in', value);
  });
  return totals;
};

const exponentAt = (step: number): number =>
  Math.round((EXPONENT_MAX - step * EXPONENT_STEP) * 100) / 100;

const pixelsPerValue = (
  input: SolveScaleInput,
  nodeTotals: Map<string, NodeTotals>
): number => {
  const sumByColumn = new Map<string, number>();
  const countByColumn = new Map<string, number>();
  for (const [id, totals] of nodeTotals) {
    const column = input.nodeGroups.get(id) ?? '';
    const value = Math.max(totals.in, totals.out);
    sumByColumn.set(column, (sumByColumn.get(column) ?? 0) + value);
    countByColumn.set(column, (countByColumn.get(column) ?? 0) + 1);
  }
  let ky = Number.POSITIVE_INFINITY;
  for (const [column, sum] of sumByColumn) {
    if (sum <= 0) continue;
    const count = countByColumn.get(column) ?? 1;
    const available = input.plotHeight - (count - 1) * input.nodePadding;
    ky = Math.min(ky, available / sum);
  }
  return Number.isFinite(ky) ? Math.max(ky, 0) : 0;
};

type Candidate = { scale: SankeyScale; thinnestPx: number };

export const solveSankeyScale = (
  input: SolveScaleInput,
  tolerance: number
): SankeyScale => {
  const rawNodeTotals = nodeTotalsFor(input.linkEnds, input.values);

  const evaluate = (scale: SankeyScale): Candidate | null => {
    const scaled = scaleLinkValues({
      values: input.values,
      sideKeys: input.sideKeys,
      scale,
    });
    const linkItems: OrderItem[] = input.values.map((raw, index) => ({
      key: `${index}`,
      group: input.linkGroups[index] ?? '',
      raw,
      rendered: scaled[index] ?? 0,
    }));
    if (findOrderInversions(linkItems, tolerance, 0).size > 0) return null;

    const scaledNodeTotals = nodeTotalsFor(input.linkEnds, scaled);
    const nodeItems: OrderItem[] = [];
    for (const [id, totals] of scaledNodeTotals) {
      const raw = rawNodeTotals.get(id) ?? { in: 0, out: 0 };
      nodeItems.push({
        key: id,
        group: input.nodeGroups.get(id) ?? '',
        raw: Math.max(raw.in, raw.out),
        rendered: Math.max(totals.in, totals.out),
      });
    }
    if (findOrderInversions(nodeItems, tolerance, 0).size > 0) return null;

    const ky = pixelsPerValue(input, scaledNodeTotals);
    const positive = scaled.filter((value) => value > 0);
    const thinnest = positive.length === 0 ? 0 : Math.min(...positive) * ky;
    return { scale, thinnestPx: thinnest };
  };

  let fallback: Candidate | null = null;
  for (let totalStep = 0; totalStep <= EXPONENT_STEPS; totalStep++) {
    for (let shareStep = 0; shareStep <= EXPONENT_STEPS; shareStep++) {
      const candidate = evaluate({
        totalExponent: exponentAt(totalStep),
        shareExponent: exponentAt(shareStep),
      });
      if (candidate === null) continue;
      if (candidate.thinnestPx >= MIN_LINK_WIDTH) return candidate.scale;
      if (fallback === null || candidate.thinnestPx > fallback.thinnestPx) {
        fallback = candidate;
      }
    }
  }
  return fallback?.scale ?? LINEAR_SANKEY_SCALE;
};
